/**
 * CombatScenarios — Automated combat tests for single-player mode.
 *
 * These tests work WITHOUT a server connection. They:
 *   1. Look up the target mob (Pig Mom) via MobSystem
 *   2. Set it as the current target via TargetingSystem
 *   3. Cast abilities via PlayerManager.performAbility()
 *   4. Read the combat log (window.__combatLog) to verify HP decreased
 *
 * Usage:
 *   // From browser console after game loads:
 *   window.__runCombatTests()
 *
 *   // Or via GameTestRunner:
 *   import { getTestRunner } from '../GameTestRunner';
 *   const runner = getTestRunner();
 *   runner.runScenario('Combat: Warrior Melee Damage');
 */

import { getTestRunner, wait, TestScenario, TestResult } from '../GameTestRunner';
import { ServiceRegistry } from '../../core/ServiceRegistry';
// Access combat log via window.__combatLog (set by DamageBridge in DEV mode)
// Avoids direct import that caused Vite module resolution issues
interface CombatLogEntry {
  seq: number;
  timestamp: number;
  event: 'melee_hit' | 'area_hit';
  abilityId: string;
  targetId: string;
  damage: number;
  hpBefore: number;
  hpAfter: number;
  applied: boolean;
  mode: 'server' | 'local';
}

function getCombatLog(): CombatLogEntry[] {
  return (globalThis as any).__combatLog ?? [];
}
import { gameLogger } from '../../../utils/GameLogger';

// ── Helpers ─────────────────────────────────────────────────────────

function getPlayerManager(): any {
  const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
  return entitySystem?.getPlayerManager() ?? null;
}

function getTargetingSystem(): any {
  return ServiceRegistry.getInstance().get('TargetingSystem') ?? null;
}

function getMobSystem(): any {
  return ServiceRegistry.getInstance().get('MobSystem', { optional: true }) ?? null;
}

/** Find the first active mob (e.g. Pig Mom) */
function findFirstMob(): { id: string; mob: any } | null {
  const mobSystem = getMobSystem();
  if (!mobSystem?.getManager()) return null;
  const manager = mobSystem.getManager();

  // Try getMobsMap, getAllMobs, or iterate
  const mobsMap: Map<string, any> | null =
    manager.getMobsMap?.() ??
    manager.getAllMobs?.() ??
    (manager.mobs instanceof Map ? manager.mobs : null);

  if (!mobsMap) return null;

  for (const [id, mob] of mobsMap) {
    if ((mob as any).active !== false && (mob as any).health > 0) {
      return { id, mob };
    }
  }
  return null;
}

/** Snapshot the current combat log sequence number so we can detect new entries */
function logSnapshot(): number {
  const log = getCombatLog();
  const last = log[log.length - 1];
  return last ? last.seq : 0;
}

/** Get new combat log entries since snapshot (sequence-based, survives ring buffer shift) */
function newLogEntries(sinceSeq: number): CombatLogEntry[] {
  return getCombatLog().filter(e => e.seq > sinceSeq);
}

/** Set up targeting for a mob */
function targetMob(mobId: string): boolean {
  const targeting = getTargetingSystem();
  if (!targeting) {
    gameLogger.error('test', '[CombatTest] TargetingSystem not found');
    return false;
  }
  targeting.setTarget(mobId);
  return true;
}

/** Cast an ability and wait for it to resolve */
async function castAbility(abilityId: string, delayMs = 600): Promise<void> {
  const pm = getPlayerManager();
  if (!pm) {
    gameLogger.error('test', `[CombatTest] PlayerManager not found, cannot cast ${abilityId}`);
    return;
  }
  pm.performAbility(abilityId);
  await wait(delayMs);
}

// ── Per-class ability lists (representative melee/projectile abilities) ──

interface ClassTest {
  className: string;
  abilities: string[];
}

const CLASS_TESTS: ClassTest[] = [
  {
    className: 'Warrior',
    abilities: ['mortal_strike', 'whirlwind', 'ground_slam'],
  },
  {
    className: 'Mage',
    abilities: ['fireball', 'frost_bolt', 'arcane_missiles'],
  },
  {
    className: 'Rogue',
    abilities: ['backstab', 'eviscerate', 'fan_of_knives'],
  },
  {
    className: 'Paladin',
    abilities: ['holy_strike', 'judgment_of_light', 'consecration'],
  },
  {
    className: 'Hunter',
    abilities: ['aimed_shot', 'multi_shot', 'explosive_shot'],
  },
];

// ── Scenario builders ───────────────────────────────────────────────

function buildClassCombatScenario(ct: ClassTest): TestScenario {
  return {
    name: `Combat: ${ct.className} Damage`,
    description: `Casts ${ct.abilities.join(', ')} and verifies HP decreases via combat log`,
    run: async (): Promise<TestResult> => {
      const startTime = performance.now();
      const details: Record<string, any> = { className: ct.className, abilities: {} };

      // 1. Find a live mob
      const target = findFirstMob();
      if (!target) {
        return {
          name: `Combat: ${ct.className} Damage`,
          passed: false,
          duration: performance.now() - startTime,
          error: 'No active mob found (is the 1v1-pig preset loaded?)',
        };
      }

      const initialHp = target.mob.health;
      details.targetId = target.id;
      details.initialHp = initialHp;

      // 2. Target the mob
      if (!targetMob(target.id)) {
        return {
          name: `Combat: ${ct.className} Damage`,
          passed: false,
          duration: performance.now() - startTime,
          error: 'Failed to target mob via TargetingSystem',
        };
      }

      // 3. Cast each ability and check the combat log
      let totalDamageApplied = 0;
      for (const abilityId of ct.abilities) {
        const snap = logSnapshot();
        await castAbility(abilityId, 800); // wait longer for projectiles

        const entries = newLogEntries(snap).filter(e => e.applied && e.targetId === target.id);
        const dmg = entries.reduce((sum, e) => sum + (e.hpBefore - e.hpAfter), 0);
        totalDamageApplied += dmg;

        details.abilities[abilityId] = {
          logEntries: entries.length,
          damageApplied: dmg,
          entries: entries.map(e => ({
            event: e.event,
            damage: e.damage,
            hpBefore: e.hpBefore,
            hpAfter: e.hpAfter,
          })),
        };

        gameLogger.info('test',
          `[CombatTest] ${ct.className}.${abilityId}: ${entries.length} hits, ${dmg} damage applied`
        );
      }

      const finalHp = target.mob.health;
      details.finalHp = finalHp;
      details.totalDamageApplied = totalDamageApplied;
      details.hpDecreased = finalHp < initialHp;

      const passed = finalHp < initialHp;

      gameLogger.info('test',
        `[CombatTest] ${ct.className} RESULT: HP ${initialHp} -> ${finalHp} ` +
        `(${totalDamageApplied} damage) — ${passed ? 'PASS' : 'FAIL'}`
      );

      return {
        name: `Combat: ${ct.className} Damage`,
        passed,
        duration: performance.now() - startTime,
        error: passed ? undefined : `HP did not decrease (${initialHp} -> ${finalHp})`,
        details,
      };
    },
  };
}

/** Build a comprehensive combat log integrity test */
function buildCombatLogIntegrityScenario(): TestScenario {
  return {
    name: 'Combat: Log Integrity',
    description: 'Verifies combat log entries have correct structure and HP math',
    run: async (): Promise<TestResult> => {
      const startTime = performance.now();
      const target = findFirstMob();
      if (!target) {
        return {
          name: 'Combat: Log Integrity',
          passed: false,
          duration: performance.now() - startTime,
          error: 'No active mob found',
        };
      }

      targetMob(target.id);
      const snap = logSnapshot();

      // Cast a known melee ability
      await castAbility('mortal_strike', 800);

      const entries = newLogEntries(snap);
      const applied = entries.filter(e => e.applied);

      const issues: string[] = [];

      for (const entry of applied) {
        if (entry.hpBefore <= 0) issues.push(`${entry.abilityId}: hpBefore is ${entry.hpBefore}`);
        if (entry.hpAfter < 0) issues.push(`${entry.abilityId}: hpAfter is ${entry.hpAfter}`);
        if (entry.hpAfter > entry.hpBefore) issues.push(`${entry.abilityId}: HP increased from ${entry.hpBefore} to ${entry.hpAfter}`);
        if (entry.damage <= 0) issues.push(`${entry.abilityId}: damage is ${entry.damage}`);
        if (!entry.abilityId) issues.push('Missing abilityId');
        if (!entry.targetId) issues.push('Missing targetId');
        if (entry.mode !== 'local' && entry.mode !== 'server') issues.push(`Invalid mode: ${entry.mode}`);
      }

      const passed = applied.length > 0 && issues.length === 0;

      return {
        name: 'Combat: Log Integrity',
        passed,
        duration: performance.now() - startTime,
        error: passed ? undefined : issues.length > 0 ? issues.join('; ') : 'No applied entries in combat log',
        details: { totalEntries: entries.length, appliedEntries: applied.length, issues },
      };
    },
  };
}

// ── Registration ────────────────────────────────────────────────────

export function registerCombatScenarios(): void {
  const runner = getTestRunner();

  // Per-class damage tests
  for (const ct of CLASS_TESTS) {
    runner.addScenario(buildClassCombatScenario(ct));
  }

  // Combat log integrity test
  runner.addScenario(buildCombatLogIntegrityScenario());

  gameLogger.info('test', `[CombatScenarios] Registered ${CLASS_TESTS.length + 1} combat test scenarios`);
}

// ── Global runner for browser console ───────────────────────────────

/**
 * Run all combat tests and print results to console.
 * Accessible from browser console via: window.__runCombatTests()
 */
export async function runCombatTests(): Promise<{
  passed: number;
  failed: number;
  total: number;
  results: TestResult[];
}> {
  gameLogger.info('test', '=== HELIX 2000 AUTOMATED COMBAT TESTS ===');
  gameLogger.info('test', 'Mode: Single-player (no server)');
  gameLogger.info('test', 'Evidence: window.__combatLog');
  gameLogger.info('test', '');

  const runner = getTestRunner();

  // Register if not already done
  const existing = runner.getAvailableScenarios();
  if (!existing.some(s => s.startsWith('Combat:'))) {
    registerCombatScenarios();
  }

  // Run only combat scenarios
  const combatScenarios = runner.getAvailableScenarios().filter(s => s.startsWith('Combat:'));
  const results: TestResult[] = [];

  for (const name of combatScenarios) {
    const result = await runner.runScenario(name);
    results.push(result);
  }

  // Summary
  const passed = results.filter(r => r.passed).length;
  const failed = results.filter(r => !r.passed).length;
  const total = results.length;

  gameLogger.info('test', '');
  gameLogger.info('test', '=== COMBAT TEST RESULTS ===');
  gameLogger.info('test', `Total: ${total} | Passed: ${passed} | Failed: ${failed}`);
  gameLogger.info('test', '');

  for (const r of results) {
    const status = r.passed ? 'PASS' : 'FAIL';
    gameLogger.info('test', `  [${status}] ${r.name} (${r.duration.toFixed(0)}ms)`);
    if (!r.passed && r.error) gameLogger.info('test', `         Error: ${r.error}`);
    if (r.details) {
      if (r.details.initialHp !== undefined) {
        gameLogger.info('test', `         HP: ${r.details.initialHp} -> ${r.details.finalHp} (${r.details.totalDamageApplied} total damage)`);
      }
    }
  }

  gameLogger.info('test', '');
  gameLogger.info('test', `Combat log entries: ${getCombatLog().length} (window.__combatLog)`);
  gameLogger.info('test', '=== END ===');

  return { passed, failed, total, results };
}

// Expose globally for browser console access
// This module is only loaded via dynamic import gated behind import.meta.env.DEV
// in SceneSetupSystem, so no additional DEV guard needed here
if (typeof globalThis !== 'undefined') {
  (globalThis as any).__runCombatTests = runCombatTests;
}

export default registerCombatScenarios;
